import { useState } from "react"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/ToggleGroup"
import type { PushProduct } from "../data"
import { type Grain, salesSeries } from "../salesSeries"
import { ActionKey } from "./chartParts"
import MovementChart from "./MovementChart"
import SalesChart, { GIFTED, SOLD } from "./SalesChart"
import StockChart, { STOCK } from "./StockChart"
import { stockSeries } from "../stockSeries"

type Graph = "movement" | "sales-gifts" | "stock"

// Selected graph gets the soft primary tint used by the page tabs.
const SELECTED = "data-[state=on]:bg-primary/10 data-[state=on]:font-semibold data-[state=on]:text-primary data-[state=on]:shadow-none"

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="h-3 w-3 rounded-sm" style={{ background: color }} />
      {label}
    </span>
  )
}

function LineLegendItem({ dashed, label }: { dashed: boolean; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <svg width="20" height="4" aria-hidden>
        <line x1="0" y1="2" x2="20" y2="2" stroke={STOCK} strokeWidth="2" strokeDasharray={dashed ? "6 4" : undefined} />
      </svg>
      {label}
    </span>
  )
}

// One graph at a time. Movement (the overall trend) shows first; Sales &
// Gifts is there for anyone who wants the split; Stock shows the level over
// time and what the incoming order does to it. Day, Week or Month applies to
// whichever graph is showing.
export default function Graphs({ product }: { product: PushProduct }) {
  const [graph, setGraph] = useState<Graph>("movement")
  const [grain, setGrain] = useState<Grain>("day")
  const points = salesSeries(product, grain)

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-4 text-sm">
        <ToggleGroup type="single" value={graph} onValueChange={(v) => v && setGraph(v as Graph)}>
          <ToggleGroupItem value="movement" size="sm" className={SELECTED}>Movement</ToggleGroupItem>
          <ToggleGroupItem value="sales-gifts" size="sm" className={SELECTED}>Sales & Gifts</ToggleGroupItem>
          <ToggleGroupItem value="stock" size="sm" className={SELECTED}>Stock</ToggleGroupItem>
        </ToggleGroup>
        <span className="flex items-center gap-4">
          {product.tried.length === 0 && <span>Nothing tried yet.</span>}
          <ToggleGroup type="single" value={grain} onValueChange={(v) => v && setGrain(v as Grain)}>
            <ToggleGroupItem value="day" size="sm" className={SELECTED}>Day</ToggleGroupItem>
            <ToggleGroupItem value="week" size="sm" className={SELECTED}>Week</ToggleGroupItem>
            <ToggleGroupItem value="month" size="sm" className={SELECTED}>Month</ToggleGroupItem>
          </ToggleGroup>
        </span>
      </div>
      {graph === "movement" && <MovementChart points={points} grain={grain} />}
      {graph === "sales-gifts" && <SalesChart points={points} grain={grain} />}
      {graph === "stock" && <StockChart points={stockSeries(product, grain)} grain={grain} />}
      <ActionKey actions={points.flatMap((p) => p.actions)} />
      {graph === "sales-gifts" && (
        <div className="mt-2 flex items-center justify-center gap-4 text-sm">
          <LegendItem color={SOLD} label="Sold" />
          <LegendItem color={GIFTED} label="Gifted" />
        </div>
      )}
      {graph === "stock" && product.incoming && (
        <div className="mt-2 flex items-center justify-center gap-4 text-sm">
          <LineLegendItem dashed={false} label="Stock" />
          <LineLegendItem dashed label="Expected, with the incoming order" />
        </div>
      )}
    </div>
  )
}
